import React, { useRef, useState } from "react";
/** Components */
import { Toasts } from "view/components/Toasts";
/** Services */
import apiLibrary from "services/api";
/** Assets */
import { Bars3Icon } from "@heroicons/react/24/outline";
import defaultBackGroundImage from "assets/images/defaultAppBackgroundImage.png";
import circleImageDefault from "assets/images/Placeholders/avatar-circle4.png";
import {
  BellIcon,
  ChatIcon,
  DocumentListIcon,
  HomeIcon,
  LogoutIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  SearchIcon,
} from "assets/icons/HeroIcons";
import {
  ArrowUpRightIcon,
  BinocularsIcon,
  CameraIcon,
  FolderListIcon,
} from "assets/icons/HeroIcons";
import ISNLogo from "assets/logo/ISNLogo";
import CommunitiesIcon from "assets/icons/HeroIcons/CommunitiesIcon";
import { setLoadingProgress } from "store/loadingBar";
import { getRandomProgress } from "utils/getRandomProgress";
import { useDispatch } from "react-redux";

interface IProps {
  setFieldValue?: any;
  values?: any;
  editMode?: boolean;
}

export const ObserverDashboardWeb: React.FC<IProps> = ({
  setFieldValue,
  values,
  editMode,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();

  /** Image upload function */
  const handleImageUpload = async (event: any) => {
    const file = event.target.files[0];
    if (file) {
      try {
        /** Validate image */
        const imageValidation = [
          "image/jpg",
          "image/jpeg",
          "image/png",
        ];
        if (!imageValidation.includes(file.type)) {
          event.target.value = "";
          return Toasts.error("Please select a valid image file (jpg/png/jpeg)");
        }

        const fileSize = file.size / 1024 / 1024;
        if (fileSize > 25) {
          return Toasts.error("File size must be less than 25 mb!");
        }
        /** Validate image dimensions */
        const dimensionsValid = await new Promise<boolean>((resolve) => {
          const img = new Image();
          img.onload = () => {
            if (img.width === 1920 && img.height === 600) {
              resolve(true);
            } else {
              Toasts.error("Image dimension must be exactly 1920 x 600 px");
              resolve(false);
            }
          };
          img.onerror = () => {
            Toasts.error("Failed to read image dimensions");
            resolve(false);
          };
          img.src = URL.createObjectURL(file);
        });

        if (!dimensionsValid) {
          event.target.value = "";
          return;
        }
        dispatch(setLoadingProgress(getRandomProgress()));
        /** Upload file */
        const uploadResponse = await apiLibrary.file.fileUpload(
          file,
          false,
          "public"
        );
        setFieldValue("observerDashboardWebImageId", uploadResponse.data);
      } catch (error: any) {
        const errorMsg = error?.response?.data?.message ?? error.message;
        Toasts.error(errorMsg);
      } finally {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        dispatch(setLoadingProgress(100));
      }
    }
  };

  /** function that trigger onclick */
  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  /** Function to set background color to default */
  const resetImage = async () => {
    try {
      dispatch(setLoadingProgress(getRandomProgress()));
      await new Promise((resolve) => setTimeout(resolve, 300));
      setFieldValue("observerDashboardWebImageId", null);
      dispatch(setLoadingProgress(100));
    } catch (error) {
      Toasts.error("Error resetting image.");
    } finally {
      dispatch(setLoadingProgress(100));
    }
  };
  return (
    <div className="w-full h-[342px] col-span-2">
      <div>
        <div>
          <p className="font-semibold text-secondaryMid dark:text-textMain text-base">
            Observer Dashboard Web
          </p>
          <p className="text-sm text-textMidLight dark:text-textMain">
            For the best results on all devices, use an image that's 1920 x 600
            px.
          </p>
          <br />
          {!editMode && (
            <button
              type="button"
              disabled={values.observerDashboardWebImageId === null}
              onClick={resetImage}
              className={`text-primary text-xs font-semibold mt-2  ${values.observerDashboardWebImageId === null ? " cursor-not-allowed " : "cursor-pointer"} `}
            >
              Reset to default
            </button>
          )}
        </div>
        <div className={`flex ${editMode ? "mt-4" : "mt-0"}`}>
          {/* Side bar */}
          <div className="h-[285px] w-[60px] bg-bgWhite dark:bg-secondaryLight px-[2px]">
            {/** Header logos */}
            <div className="flex justify-between py-1 items-start">
              <div>
                <Bars3Icon className="w-3 h-3 dark:stroke-caption" />
              </div>
              <div className="flex justify-start mt-[2px] mr-[9px]">
                {" "}
                <ISNLogo width={8} height={8} />
              </div>
              <div>
                <div className="flex flex-col items-center">
                  <BellIcon className="w-3 h-3 relative" />
                  <div className="absolute h-[4px] w-[4px] rounded-full bg-bgWhite mt-[2px] ml-[4px]">
                    <div className="h-[3px] w-[3px] rounded-full bg-primary"></div>
                  </div>
                </div>
              </div>
            </div>
            {/** Name and Image Section */}
            <div className="flex items-center gap-1">
              <img
                src={circleImageDefault}
                alt=""
                className="w-3 h-3 rounded-full"
              />
              <p className="text-secondaryMid dark:text-textWhite text-[4px] font-semibold">
                Aaron Lestenkof
              </p>
            </div>
            {/** Search Section */}
            <div className="pt-[3px]">
              <div className="w-full py-[2px] border-lineDark border flex items-center px-1 gap-1 rounded-sm">
                <span>
                  <SearchIcon className=" w-2 h-2 " />
                </span>
                <span className="text-textLightExtra text-[4px]">Search</span>
              </div>
            </div>
            <div className="pt-[3px]">
              <div className="w-full bg-primary flex py-[3px] rounded-md items-center justify-center gap-1">
                <PlusIcon className="w-1 h-1" fill="#FFFFFF" />
                <span className="text-[3.7px] text-textWhite font-semibold ">
                  New Observation
                </span>
              </div>
            </div>
            <div className="pt-[1px]">
              <div className="flex items-center gap-1 py-[2px]  px-[1px]">
                <span>
                  <HomeIcon className="w-2 h-2" fill="#2C3236" />
                </span>
                <span className="text-[4px] text-secondaryMid dark:text-textWhite">
                  Home
                </span>
              </div>
              <div className="flex items-center gap-1 py-[2px]  px-[1px]">
                <span>
                  <BinocularsIcon className="w-2 h-2" fill="#2C3236" />
                </span>
                <span className="text-[4px] text-secondaryMid dark:text-textWhite">
                  Observations
                </span>
              </div>
              <div className="flex items-center gap-1 py-[2px]  px-[1px]">
                <span>
                  <FolderListIcon className="w-2 h-2" fill="#2C3236" />
                </span>
                <span className="text-[4px] text-secondaryMid dark:text-textWhite">
                  Programs
                </span>
              </div>
              <div className="flex items-center gap-1 py-[2px]  px-[1px]">
                <span>
                  <DocumentListIcon className="w-2 h-2" fill="#2C3236" />
                </span>
                <span className="text-[4px] text-secondaryMid dark:text-textWhite">
                  Forms
                </span>
              </div>
              <div className="flex items-center gap-1 py-[2px]  px-[1px]">
                <span>
                  <CommunitiesIcon className="w-2 h-2" fill="#2C3236" />
                </span>
                <span className="text-[4px] text-secondaryMid dark:text-textWhite">
                  Communities
                </span>
              </div>
              <div className="flex items-center gap-1 py-[2px]  px-[1px]">
                <span>
                  <ChatIcon className="w-2 h-2" fill="#2C3236" />
                </span>
                <span className="text-[4px] text-secondaryMid dark:text-textWhite">
                  Messages
                </span>
              </div>
              <div className="border-t border-borderGreyishLight_7 mt-[3px]"></div>
              <div className="flex items-center gap-1 py-[2px]  px-[1px] mt-[3px]">
                <span>
                  <QuestionMarkCircleIcon className="w-2 h-2" fill="#2C3236" />
                </span>
                <span className="text-[4px] text-secondaryMid dark:text-textWhite">
                  Help and support
                </span>
              </div>
              <div className="flex items-center gap-1 py-[2px]  px-[1px]">
                <span>
                  <LogoutIcon className="w-2 h-2" fill="#2C3236" />
                </span>
                <span className="text-[4px] text-secondaryMid dark:text-textWhite">
                  Sign out
                </span>
              </div>
            </div>
          </div>
          {/* Image section */}
          <div className="relative w-[389px] bg-primary_9 dark:bg-bgMain">
            <div
              className="absolute inset-0 bg-cover bg-center h-[155px] z-10"
              style={{
                backgroundImage: `url(${values.observerDashboardWebImageId?.file ? `"${values.observerDashboardWebImageId?.file.original}"` : defaultBackGroundImage})`,
              }}
            >
            </div>

            <div className="px-1 py-1 flex justify-between items-center text-white text-sm relative z-10">
              <span className="text-[7.52px] text-textWhite relative top-[8px] left-[10px]">
                9:30
              </span>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              {!editMode && (
                <button
                  type="button"
                  onClick={triggerImageUpload}
                  className="h-8 w-8 disabled:cursor-not-allowed flex justify-center items-center rounded-full bg-bgWhite dark:bg-secondaryLight absolute right-[-10px] top-[-12px] z-20"
                >
                  <CameraIcon className="w-5 h-5" />
                </button>
              )}
              <div className="z-10 relative right-[10px] top-[4px]">
                <button
                  type="button"
                  className="bg-primary flex items-center justify-center p-[3px] rounded-lg gap-1"
                >
                  {" "}
                  <span>
                    <PlusIcon className="w-2.5 h-2.5" fill="#FFFFFF" />
                  </span>{" "}
                  <span className="text-[5.83px] text-textWhite font-bold">
                    Add Observation
                  </span>{" "}
                </button>
              </div>
            </div>
            <div className=" px-2 w-full relative z-20 top-[15px]">
              <div className="flex justify-evenly w-full">
                <div className="bg-primaryLightSilver rounded-md h-[33.37px] w-[32%] relative shadow-lg">
                  <p className="text-textWhite text-[3.5px] absolute top-[2px] px-[3px]">
                    Number Of Observations
                  </p>
                  <p className="text-textWhite text-sm absolute top-[4px] px-[3px]">
                    310
                  </p>
                  <div className="absolute bottom-[3px] right-[3px] w-3 h-3 bg-bgGreyish_77 rounded-full flex items-center justify-center">
                    <ArrowUpRightIcon className="w-2 h-2" fill="#FFFFFF" />
                  </div>
                </div>
                <div className="bg-primaryLightSilver rounded-md h-[33.37px] w-[32%] relative shadow-lg">
                  <p className="text-textWhite text-[3.5px] absolute top-[2px] px-[3px]">
                    In Review
                  </p>
                  <p className="text-textWhite text-sm absolute top-[4px] px-[3px]">
                    25
                  </p>
                  <div className="absolute bottom-[3px] right-[3px] w-3 h-3 bg-bgGreyish_77 rounded-full flex items-center justify-center">
                    <ArrowUpRightIcon className="w-2 h-2" fill="#FFFFFF" />
                  </div>
                </div>
                <div className="bg-primaryLightSilver rounded-md h-[33.37px] w-[32%] relative shadow-lg">
                  <p className="text-textWhite text-[3.5px] absolute top-[2px] px-[3px]">
                    Draft
                  </p>
                  <p className="text-textWhite text-sm absolute top-[4px] px-[3px]">
                    310
                  </p>
                  <div className="absolute bottom-[3px] right-[3px] w-3 h-3 bg-bgGreyish_77 rounded-full flex items-center justify-center">
                    <ArrowUpRightIcon className="w-2 h-2" fill="#FFFFFF" />
                  </div>
                </div>
              </div>
            </div>
            <div className=" px-2 w-full relative z-20 top-7">
              <div className="flex justify-evenly w-full">
                <div className="rounded-md h-[137px] w-[32%] relative shadow-lg bg-bgWhite dark:bg-secondaryLight"></div>
                <div className="rounded-md h-[137px] w-[32%] relative shadow-lg bg-bgWhite dark:bg-secondaryLight"></div>
                <div className="rounded-md h-[137px] w-[32%] relative shadow-lg bg-bgWhite dark:bg-secondaryLight"></div>
              </div>
            </div>
            <div className="px-2 w-full relative z-20 top-11">
              <div className="flex flex-col w-full px-1">
                <div className="h-[15px] w-full relative bg-bgWhite dark:bg-secondaryLight border-b border-borderLightGray dark:border-secondaryMid "></div>
                <div className="h-[15px] w-full relative bg-bgWhite dark:bg-secondaryLight border-b border-borderLightGray dark:border-secondaryMid "></div>
                <div className="h-[15px] w-full relative bg-bgWhite dark:bg-secondaryLight border-b border-borderLightGray dark:border-secondaryMid "></div>
                <div className="h-[5px] w-full relative bg-bgWhite dark:bg-secondaryLight "></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
